"use client";

import { useState, useEffect, Suspense } from "react";
import { useAuth } from "../../lib/hooks/useAuth";
import { ChatList } from "../../components/chat/ChatList";
import { EnhancedChatModal } from "../../components/chat/EnhancedChatModal"; // 우리가 개발한 완전한 기능
import { ProtectedRoute } from "../../lib/auth/ProtectedRoute";
import { MessageCircle, Plus, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { useRouter, useSearchParams } from "next/navigation";
import { ErrorBoundary } from "../../components/ui/ErrorBoundary";

function ChatPageContent() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isDevMode, setIsDevMode] = useState(false);

  // 개발용 더미 로그인
  const handleDevLogin = () => {
    setIsDevMode(true);
    // 개발용 더미 사용자 데이터 설정
    const dummyUser = {
      uid: process.env.NEXT_PUBLIC_DEV_DUMMY_USER_ID || "demo-user-123",
      username: process.env.NEXT_PUBLIC_DEV_DUMMY_USERNAME || "testuser",
      nickname: process.env.NEXT_PUBLIC_DEV_DUMMY_NICKNAME || "테스트유저",
      email: "test@connectone.local",
      region: "서울",
      grade: "C",
      tradesCount: 0,
      reviewsCount: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // 로컬 스토리지에 더미 사용자 저장
    localStorage.setItem("dev-dummy-user", JSON.stringify(dummyUser));
    window.location.reload();
  };

  console.log("ChatPage 렌더링:", {
    user: user?.uid,
    selectedChatId,
    showChatModal,
    isClient,
  });

  // 클라이언트 사이드에서만 실행되도록 보장
  useEffect(() => {
    setIsClient(true);

    // 배포 환경 안전장치 - Firebase 초기화 확인
    const checkDeploymentReady = () => {
      try {
        // Firebase 환경변수 확인
        const hasRequiredEnvVars =
          process.env.NEXT_PUBLIC_FIREBASE_API_KEY ||
          typeof window !== "undefined";

        if (hasRequiredEnvVars) {
          console.log("✅ 배포 환경 준비 완료");
        } else {
          console.warn("⚠️ Firebase 환경변수 누락 - 재시도 중...");
          setTimeout(checkDeploymentReady, 1000);
        }
      } catch (error) {
        console.error("❌ 배포 환경 확인 실패:", error);
        setTimeout(checkDeploymentReady, 2000);
      }
    };

    checkDeploymentReady();
  }, []);

  // URL에서 chatId를 가져와서 자동으로 채팅 모달 열기
  useEffect(() => {
    if (!isClient) return;

    const chatId = searchParams.get("chatId");
    if (chatId && user) {
      console.log("URL에서 chatId 발견:", chatId);
      setSelectedChatId(chatId);
      setShowChatModal(true);
    }
  }, [searchParams, user, isClient]);

  const handleChatSelect = (chatId: string) => {
    if (!isClient) return;

    console.log("ChatPage: handleChatSelect 호출됨", { chatId });
    setSelectedChatId(chatId);
    setShowChatModal(true);
    console.log("ChatPage: 모달 상태 업데이트됨", {
      selectedChatId: chatId,
      showChatModal: true,
    });
  };

  const handleCloseModal = () => {
    setShowChatModal(false);
    setSelectedChatId(null);
  };

  const handleChatDeleted = () => {
    setShowChatModal(false);
    setSelectedChatId(null);
  };

  if (!isClient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <ProtectedRoute>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                채팅 기능을 사용하려면
              </h2>
              <p className="text-gray-600 mb-6">로그인이 필요합니다</p>

              {/* 개발용 더미 로그인 버튼 */}
              <div className="space-y-4">
                <Button
                  onClick={handleDevLogin}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={isDevMode}
                >
                  {isDevMode ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      로그인 중...
                    </>
                  ) : (
                    "🧪 개발용 테스트 로그인"
                  )}
                </Button>

                <div className="text-sm text-gray-500">또는</div>

                <Button
                  onClick={() => router.push("/auth/login")}
                  variant="outline"
                  className="w-full"
                >
                  일반 로그인
                </Button>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>개발 모드:</strong> 테스트용 더미 계정으로 채팅 기능을
                  체험해보세요!
                </p>
              </div>
            </div>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50 pb-24 md:pb-0">
        {/* 헤더 */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                  채팅
                </h1>
              </div>
              <Button
                onClick={() => router.push("/list")}
                variant="outline"
                size="sm"
                className="text-xs sm:text-sm"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">상품 둘러보기</span>
                <span className="sm:hidden">둘러보기</span>
              </Button>
            </div>
          </div>
        </div>

        {/* 채팅 목록 */}
        <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6">
          <ErrorBoundary>
            <ChatList
              onChatSelect={handleChatSelect}
              onChatDeleted={handleChatDeleted}
            />
          </ErrorBoundary>
        </div>

        {/* 우리가 개발한 완전한 채팅 모달 - 모든 기능 포함 */}
        {selectedChatId && showChatModal && isClient && (
          <div key={selectedChatId}>
            <EnhancedChatModal
              isOpen={true}
              onClose={handleCloseModal}
              chatId={selectedChatId}
              onChatDeleted={handleChatDeleted}
            />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default function ChatPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      }
    >
      <ChatPageContent />
    </Suspense>
  );
}
